import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Tag,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { FiCheck, FiInfo, FiMoreVertical, FiX } from 'react-icons/fi';
import { useAuth } from 'contexts/AuthContext';
import { auth } from '../../../../firebase/config';
import { ORG_ADMIN_ONLY_TOOLTIP } from 'services/firebaseAccountLookup';
import {
  OrgMemberRole,
  PendingMembershipRequest,
  TeamMember,
  teamMembersService,
} from 'services/teamMembersService';
import FirebaseAnalyticsService from 'services/firebaseAnalyticsService';

function initialsFromEmail(email: string): string | null {
  if (!email || email === '—') return null;
  const at = email.indexOf('@');
  const local = (at === -1 ? email : email.slice(0, at)).replace(
    /[^a-zA-Z0-9]/g,
    '',
  );
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  if (local.length === 1) return `${local[0]}${local[0]}`.toUpperCase();
  return null;
}

/** First + last initial from name; falls back to email local-part; single word → first two letters. */
function initialsFromDisplayName(displayName: string, email?: string): string {
  const t = displayName.trim();
  if (!t || t === '—') {
    return initialsFromEmail(email ?? '') ?? '?';
  }
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = Array.from(parts[0])[0] ?? '';
    const last = Array.from(parts[parts.length - 1])[0] ?? '';
    const pair = `${first}${last}`.toUpperCase();
    if (pair.replace(/\s/g, '').length > 0) return pair;
  }
  const w = parts[0] ?? '';
  if (w.length > 0) {
    const u = w.toUpperCase();
    const g = Array.from(u);
    if (g.length >= 2) return `${g[0]}${g[1]}`;
    if (g.length === 1) return `${g[0]}${g[0]}`;
  }
  return initialsFromEmail(email ?? '') ?? '?';
}

type UnifiedRow =
  | {
      kind: 'pending';
      rowKey: string;
      displayName: string;
      email: string;
      roleLabel: string;
      statusLabel: string;
      sortDate: number;
      requesterUid: string;
      requestId: string;
    }
  | {
      kind: 'member';
      rowKey: string;
      displayName: string;
      email: string;
      roleLabel: string;
      statusLabel: string;
      sortDate: number;
      requestId: null;
      memberMeta: TeamMember;
    };

function buildRows(
  members: TeamMember[],
  pending: PendingMembershipRequest[],
): UnifiedRow[] {
  const memberRows: UnifiedRow[] = members.map((m) => {
    const statusLabel = m.disabled ? 'Disabled' : 'Active';
    const row: UnifiedRow = {
      kind: 'member',
      rowKey: `m:${m.uid}`,
      displayName: m.displayName || '—',
      email: m.email || '—',
      roleLabel: m.isTenantAdmin ? 'Admin' : 'Member',
      statusLabel,
      sortDate: 0,
      requestId: null,
      memberMeta: m,
    };
    return row;
  });

  const pendingRows: UnifiedRow[] = pending.map((p) => {
    const ts = p.createdAt;
    const sortDate =
      ts && typeof ts._seconds === 'number' ? ts._seconds * 1000 : 0;
    return {
      kind: 'pending',
      rowKey: `p:${p.id}`,
      displayName: p.displayName || '—',
      email: p.requesterEmail || '—',
      roleLabel: '—',
      statusLabel: 'Pending',
      sortDate,
      requesterUid: p.requesterUid ?? '',
      requestId: p.id,
    };
  });

  pendingRows.sort((a, b) => b.sortDate - a.sortDate);
  return [...pendingRows, ...memberRows];
}

function isRowCurrentUser(
  row: UnifiedRow,
  currentUid: string | undefined,
): boolean {
  if (!currentUid) return false;
  if (row.kind === 'member') return row.memberMeta.uid === currentUid;
  return row.requesterUid === currentUid;
}

const ORG_ROLE_PERMISSION_ROWS: {
  action: string;
  adminAllowed: boolean;
  memberAllowed: boolean;
}[] = [
  {
    action: 'Use the app with your organization',
    adminAllowed: true,
    memberAllowed: true,
  },
  { action: 'Send partner requests', adminAllowed: true, memberAllowed: true },
  {
    action: 'Update company overview',
    adminAllowed: true,
    memberAllowed: true,
  },
  {
    action: 'Update ICP settings',
    adminAllowed: true,
    memberAllowed: true,
  },
  {
    action: 'Leave the organization (remove yourself)',
    adminAllowed: true,
    memberAllowed: true,
  },
  {
    action: 'Connect a HubSpot Portal',
    adminAllowed: true,
    memberAllowed: true,
  },
  {
    action: 'Accept / Decline or Disconnect partner connections',
    adminAllowed: true,
    memberAllowed: false,
  },
  {
    action: 'Review approve / decline user join requests',
    adminAllowed: true,
    memberAllowed: false,
  },
  {
    action: 'Remove users',
    adminAllowed: true,
    memberAllowed: false,
  },
  {
    action: 'Change user roles',
    adminAllowed: true,
    memberAllowed: false,
  },
  {
    action: 'Disconnect a HubSpot Portal',
    adminAllowed: true,
    memberAllowed: false,
  },
  {
    action: 'Delete the organization',
    adminAllowed: true,
    memberAllowed: false,
  },
];

function TeamMemberIdentityBlock(props: {
  displayName: string;
  email: string;
  textColor: string;
  secondaryText: string;
  authLookupFailed?: boolean;
}) {
  const { displayName, email, textColor, secondaryText, authLookupFailed } =
    props;
  const initials = initialsFromDisplayName(displayName, email);
  const avatarRing = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');

  return (
    <Flex align="flex-start" gap={3} py={1} maxW="320px">
      <Box
        w="44px"
        h="44px"
        minW="44px"
        minH="44px"
        borderRadius="full"
        flexShrink={0}
        bg="purple.600"
        bgGradient="linear(to-br, blue.500, purple.500)"
        borderWidth="1px"
        borderColor={avatarRing}
        display="flex"
        alignItems="center"
        justifyContent="center"
        userSelect="none"
        boxShadow="sm"
      >
        <Text
          as="span"
          fontWeight="800"
          color="white"
          fontSize="md"
          letterSpacing="tight"
          lineHeight="1"
          textShadow="0 1px 2px rgba(0, 0, 0, 0.45)"
        >
          {initials}
        </Text>
      </Box>
      <Flex direction="column" align="flex-start" gap={0.5} minW={0} flex={1}>
        <Flex align="center" gap={2} flexWrap="wrap" minW={0}>
          <Text color={textColor} fontSize="md" fontWeight="600" noOfLines={2}>
            {displayName}
          </Text>
          {authLookupFailed ? (
            <Tooltip label="Firebase Auth details could not be loaded for this user.">
              <Badge colorScheme="orange" fontSize="0.65rem" borderRadius="md">
                Lookup failed
              </Badge>
            </Tooltip>
          ) : null}
        </Flex>
        <Text
          fontSize="sm"
          fontWeight="500"
          color={secondaryText}
          noOfLines={2}
          wordBreak="break-word"
        >
          {email}
        </Text>
      </Flex>
    </Flex>
  );
}

const ROLE_SUBMENU_CLOSE_MS = 220;

type ChangeRoleSubmenuProps = {
  member: TeamMember;
  updatingRoleUid: string | null;
  onSelectRole: (role: OrgMemberRole) => void;
};

function ChangeRoleSubmenu({
  member,
  updatingRoleUid,
  onSelectRole,
}: ChangeRoleSubmenuProps) {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const roleDropdownTextColor = useColorModeValue('black', 'white');
  const roleMenuItemHoverBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const roleMenuItemDisabledTextColor = useColorModeValue(
    'gray.500',
    'gray.400',
  );

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
    }, ROLE_SUBMENU_CLOSE_MS);
  }, [cancelScheduledClose]);

  const openSubmenu = useCallback(() => {
    cancelScheduledClose();
    setOpen(true);
  }, [cancelScheduledClose]);

  useEffect(
    () => () => {
      cancelScheduledClose();
    },
    [cancelScheduledClose],
  );

  const isBusy = updatingRoleUid === member.uid;
  const currentAdmin = Boolean(member.isTenantAdmin);
  const isAdminCurrent = currentAdmin && !isBusy;
  const isMemberCurrent = !currentAdmin && !isBusy;

  return (
    <Menu
      placement="right-start"
      closeOnSelect
      isOpen={open}
      onClose={() => setOpen(false)}
      strategy="fixed"
      gutter={4}
      isLazy
    >
      <MenuButton
        as={MenuItem}
        isDisabled={isBusy}
        onMouseEnter={openSubmenu}
        onMouseLeave={scheduleClose}
        onFocus={openSubmenu}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          color: roleDropdownTextColor,
          _hover: { color: roleDropdownTextColor },
          _focus: { color: roleDropdownTextColor },
          _active: { color: roleDropdownTextColor },
          _disabled: {
            color: roleMenuItemDisabledTextColor,
            opacity: 1,
          },
        }}
      >
        <HStack
          flex={1}
          justify="space-between"
          w="100%"
          color={roleDropdownTextColor}
        >
          <Text as="span">Change role</Text>
          <ChevronRightIcon color="currentColor" />
        </HStack>
      </MenuButton>
      <MenuList
        minW="220px"
        zIndex={2500}
        onMouseEnter={openSubmenu}
        onMouseLeave={scheduleClose}
      >
        <MenuItem
          isDisabled={isAdminCurrent || isBusy}
          onClick={(e) => {
            e.stopPropagation();
            if (isAdminCurrent || isBusy) return;
            onSelectRole('admin');
          }}
          sx={{
            color: roleDropdownTextColor,
            _hover: {
              color: roleDropdownTextColor,
              bg: roleMenuItemHoverBg,
            },
            _focus: {
              color: roleDropdownTextColor,
              bg: roleMenuItemHoverBg,
            },
            _disabled: {
              opacity: 1,
              color: roleMenuItemDisabledTextColor,
              cursor: 'not-allowed',
              _hover: {
                bg: roleMenuItemHoverBg,
                color: roleMenuItemDisabledTextColor,
              },
            },
          }}
        >
          <HStack spacing={2} flex={1}>
            <Text as="span" color="inherit">
              Admin
            </Text>
            {currentAdmin ? (
              <Text as="span" color="inherit" fontWeight="500" fontSize="sm">
                (Current)
              </Text>
            ) : null}
          </HStack>
        </MenuItem>
        <MenuItem
          isDisabled={isMemberCurrent || isBusy}
          onClick={(e) => {
            e.stopPropagation();
            if (isMemberCurrent || isBusy) return;
            onSelectRole('member');
          }}
          sx={{
            color: roleDropdownTextColor,
            _hover: {
              color: roleDropdownTextColor,
              bg: roleMenuItemHoverBg,
            },
            _focus: {
              color: roleDropdownTextColor,
              bg: roleMenuItemHoverBg,
            },
            _disabled: {
              opacity: 1,
              color: roleMenuItemDisabledTextColor,
              cursor: 'not-allowed',
              _hover: {
                bg: roleMenuItemHoverBg,
                color: roleMenuItemDisabledTextColor,
              },
            },
          }}
        >
          <HStack spacing={2} flex={1}>
            <Text as="span" color="inherit">
              Member
            </Text>
            {!currentAdmin ? (
              <Text as="span" color="inherit" fontWeight="500" fontSize="sm">
                (Current)
              </Text>
            ) : null}
          </HStack>
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

/** Role pill: light gray background aligned with `TeamStatusTag` layout. */
function TeamRoleTag({ label }: { label: string }) {
  const pillBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const pillText = useColorModeValue('gray.700', 'gray.100');

  return (
    <Tag
      fontSize="xs"
      h="32px"
      mb="6px"
      me="6px"
      px="14px"
      borderRadius="10px"
      bg={pillBg}
    >
      <Flex align="center" justify="center" gap={1.5}>
        <Text color={pillText} fontSize="14px" fontWeight="700">
          {label}
        </Text>
      </Flex>
    </Tag>
  );
}

/** Status chips aligned with Ecosystem `ConnectedPartnerTable` / `StatusTag`. */
function TeamStatusTag({ label }: { label: string }) {
  let bg = 'gray.100';
  let color = 'gray.500';
  let text = label;

  switch (label) {
    case 'Pending':
      bg = 'yellow.100';
      color = 'yellow.700';
      break;
    case 'Active':
      bg = 'green.100';
      color = 'green.600';
      text = 'Active';
      break;
    case 'Disabled':
      bg = 'gray.100';
      color = 'gray.400';
      text = 'Disabled';
      break;
    default:
      break;
  }

  return (
    <Tag
      fontSize="xs"
      h="32px"
      mb="6px"
      me="6px"
      px="14px"
      borderRadius="10px"
      bg={bg}
    >
      <Flex align="center" gap={1.5}>
        {label === 'Active' ? (
          <Box
            w="8px"
            h="8px"
            borderRadius="full"
            bg="green.500"
            flexShrink={0}
            aria-hidden
          />
        ) : null}
        <Text color={color} fontSize="14px" fontWeight="700">
          {text}
        </Text>
      </Flex>
    </Tag>
  );
}

export const Team = () => {
  const authCtx = useAuth();
  const { token, user, logout, isOrgAdmin } = authCtx ?? {};
  const toast = useToast();
  const textColor = useColorModeValue('navy.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const secondaryText = useColorModeValue(
    'secondaryGray.600',
    'secondaryGray.500',
  );
  const youRowBg = useColorModeValue('brand.50', 'whiteAlpha.50');
  const leaveModalBodyColor = useColorModeValue('black', 'white');
  const rolesModalTableBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const rolesModalHeaderBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const rolesModalRowHoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');

  const {
    isOpen: isRolesInfoOpen,
    onOpen: onOpenRolesInfo,
    onClose: onCloseRolesInfo,
  } = useDisclosure();

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [pendingRequests, setPendingRequests] = useState<
    PendingMembershipRequest[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null);
  const [removingUid, setRemovingUid] = useState<string | null>(null);
  const [updatingRoleUid, setUpdatingRoleUid] = useState<string | null>(null);

  const canRemoveMember = useCallback(
    (member: TeamMember) => {
      if (!user?.uid) return false;
      if (member.uid === user.uid) return true;
      return Boolean(isOrgAdmin);
    },
    [isOrgAdmin, user?.uid],
  );

  const load = useCallback(
    async (tokenOverride?: string | null) => {
      const effectiveToken = tokenOverride ?? token;
      if (!effectiveToken) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data } =
          await teamMembersService.getTeamMembers(effectiveToken);
        setMembers(data.members ?? []);
        setPendingRequests(data.pendingRequests ?? []);
      } catch (err: unknown) {
        const ax = err as {
          response?: { data?: { error?: { message?: string } } };
        };
        const msg =
          ax.response?.data?.error?.message ?? 'Could not load team members.';
        toast({
          title: msg,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        setMembers([]);
        setPendingRequests([]);
      } finally {
        setLoading(false);
      }
    },
    [token, toast],
  );

  useEffect(() => {
    FirebaseAnalyticsService.trackNavigation('Team', '/admin/team');
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(
    () => buildRows(members, pendingRequests),
    [members, pendingRequests],
  );

  const handleAccept = async (requestId: string) => {
    if (!token) return;
    setActionId(requestId);
    try {
      await teamMembersService.acceptRequest(token, requestId);
      FirebaseAnalyticsService.trackFeatureUsage(
        'Team',
        'accept_join_request_success',
      );
      toast({
        title: 'Access approved',
        description: 'The user can sign in with their email and password.',
        status: 'success',
        colorScheme: 'brand',
        duration: 5000,
        isClosable: true,
      });
      await load();
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { error?: { message?: string } } };
      };
      FirebaseAnalyticsService.trackFeatureUsage(
        'Team',
        'accept_join_request_failed',
      );
      toast({
        title:
          ax.response?.data?.error?.message ?? 'Could not approve the request.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setActionId(null);
    }
  };

  const handleDecline = async (requestId: string) => {
    if (!token) return;
    setActionId(requestId);
    try {
      await teamMembersService.declineRequest(token, requestId);
      FirebaseAnalyticsService.trackFeatureUsage(
        'Team',
        'decline_join_request_success',
      );
      toast({
        title: 'Request declined',
        status: 'warning',
        colorScheme: 'horizonRed',
        duration: 4000,
        isClosable: true,
      });
      await load();
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { error?: { message?: string } } };
      };
      FirebaseAnalyticsService.trackFeatureUsage(
        'Team',
        'decline_join_request_failed',
      );
      toast({
        title:
          ax.response?.data?.error?.message ?? 'Could not decline the request.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setActionId(null);
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!token || !memberToRemove) return;
    const targetUid = memberToRemove.uid;
    const isSelf = targetUid === user?.uid;
    setRemovingUid(targetUid);
    try {
      const { data } = await teamMembersService.removeTeamMember(
        token,
        targetUid,
      );
      FirebaseAnalyticsService.trackFeatureUsage(
        'Team',
        isSelf ? 'leave_organization_success' : 'remove_member_success',
      );
      setMemberToRemove(null);
      toast({
        title: isSelf ? 'You left the organization' : 'Member removed',
        description: isSelf
          ? 'You no longer have access to this organization.'
          : `${memberToRemove.displayName || 'This user'} no longer has access.`,
        status: 'success',
        colorScheme: 'brand',
        duration: 5000,
        isClosable: true,
      });
      if (data.firebaseUserDeleted && data.targetFirebaseUserId === user?.uid) {
        toast({
          title: 'Sign-in ended',
          description:
            'Your credentials were removed. Sign in again if you still have access elsewhere.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
        await logout();
      } else {
        await load();
      }
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { error?: { message?: string } } };
      };
      FirebaseAnalyticsService.trackFeatureUsage(
        'Team',
        isSelf ? 'leave_organization_failed' : 'remove_member_failed',
      );
      toast({
        title:
          ax.response?.data?.error?.message ?? 'Could not remove team member.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setRemovingUid(null);
    }
  };

  const applyMemberRole = useCallback(
    async (member: TeamMember, role: OrgMemberRole) => {
      if (!token) return;
      const currentRole: OrgMemberRole = member.isTenantAdmin
        ? 'admin'
        : 'member';
      if (role === currentRole) return;
      setUpdatingRoleUid(member.uid);
      try {
        await teamMembersService.updateMemberRole(token, member.uid, role);
        const isSelf = member.uid === user?.uid;
        const displayName = member.displayName;
        FirebaseAnalyticsService.trackFeatureUsage(
          'Team',
          'change_role_success',
          {
            new_role: role,
            is_self: isSelf,
          },
        );
        toast({
          title: 'Role updated',
          description: isSelf
            ? 'Your organization role has been updated.'
            : `${displayName || 'This user'} is now ${
                role === 'admin' ? 'an admin' : 'a member'
              }.`,
          status: 'success',
          colorScheme: 'brand',
          duration: 5000,
          isClosable: true,
        });
        if (isSelf) {
          const fresh = await auth.currentUser?.getIdToken(true);
          await load(fresh ?? undefined);
        } else {
          await load();
        }
      } catch (err: unknown) {
        const ax = err as {
          response?: { data?: { error?: { message?: string } } };
        };
        FirebaseAnalyticsService.trackFeatureUsage(
          'Team',
          'change_role_failed',
          { new_role: role },
        );
        toast({
          title:
            ax.response?.data?.error?.message ??
            'Could not update member role.',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      } finally {
        setUpdatingRoleUid(null);
      }
    },
    [token, user?.uid, toast, load],
  );

  if (!token) {
    return (
      <Center w="100%" h="50vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="brand.400"
          size="xl"
        />
      </Center>
    );
  }

  return (
    <Box w="100%" minH="100vh" mt="100px" px={{ base: 2, md: 8 }} py={4}>
      <Card w="100%" p="20px" borderRadius="30px">
        <Modal
          isOpen={isRolesInfoOpen}
          onClose={onCloseRolesInfo}
          isCentered
          size="xl"
        >
          <ModalOverlay />
          <ModalContent borderRadius="16px" maxW="720px">
            <ModalHeader color={textColor} px={6} pr={12} pt={4} pb={0}>
              Organizational roles
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6} pt={2} px={6}>
              <Text fontSize="sm" color={secondaryText} mb={8}>
                Admins can manage membership, partners, and organization
                settings. Members can collaborate but cannot change org-wide
                configuration or other people&apos;s access.
              </Text>
              <Box overflowX="auto">
                <Table variant="simple" size="sm" sx={{ tableLayout: 'fixed' }}>
                  <Thead>
                    <Tr bg={rolesModalHeaderBg}>
                      <Th
                        borderColor={rolesModalTableBorder}
                        color={textColor}
                        w="52%"
                      >
                        Action
                      </Th>
                      <Th
                        borderColor={rolesModalTableBorder}
                        textAlign="center"
                        w="24%"
                        color={textColor}
                      >
                        Admin
                      </Th>
                      <Th
                        borderColor={rolesModalTableBorder}
                        textAlign="center"
                        w="24%"
                        color={textColor}
                      >
                        Member
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {ORG_ROLE_PERMISSION_ROWS.map((row) => (
                      <Tr
                        key={row.action}
                        _hover={{ bg: rolesModalRowHoverBg }}
                        transition="background 0.15s ease"
                      >
                        <Td
                          borderColor={rolesModalTableBorder}
                          color={leaveModalBodyColor}
                          verticalAlign="top"
                        >
                          {row.action}
                        </Td>
                        <Td
                          borderColor={rolesModalTableBorder}
                          textAlign="center"
                          verticalAlign="middle"
                        >
                          {row.adminAllowed ? '✓' : '—'}
                        </Td>
                        <Td
                          borderColor={rolesModalTableBorder}
                          textAlign="center"
                          verticalAlign="middle"
                        >
                          {row.memberAllowed ? '✓' : '—'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              <Text fontSize="xs" color={secondaryText} mt={3}>
                Legend: ✓ = allowed · — = not allowed
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onCloseRolesInfo}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Box mb={6}>
          <Flex
            align="center"
            justify="space-between"
            gap={3}
            flexWrap="wrap"
            rowGap={2}
          >
            <Text fontSize="2xl" fontWeight="bold" color={textColor}>
              Team
            </Text>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="gray"
              leftIcon={<FiInfo />}
              onClick={onOpenRolesInfo}
              flexShrink={0}
            >
              Role permissions
            </Button>
          </Flex>
          <Text fontSize="sm" color={secondaryText} mt={1}>
            Active members and pending access requests for your organization.
          </Text>
        </Box>

        {loading && rows.length === 0 ? (
          <Flex justify="center" py={16}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="brand.400"
              size="lg"
            />
          </Flex>
        ) : rows.length === 0 && !loading ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={12}
            px={6}
            textAlign="center"
          >
            <Text fontSize="lg" fontWeight="600" color={textColor} mb={2}>
              No team members yet
            </Text>
            <Text fontSize="md" color="gray.500" maxW="400px" lineHeight="1.6">
              When teammates sign up or request access, they will appear here.
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" w="100%" overflowX={{ sm: 'scroll' }}>
            <Modal
              isOpen={memberToRemove !== null}
              onClose={() => setMemberToRemove(null)}
              isCentered
              size="md"
            >
              <ModalOverlay />
              <ModalContent borderRadius="16px">
                <ModalHeader color={textColor} pr={12}>
                  {memberToRemove?.uid === user?.uid
                    ? 'Leave this organization?'
                    : 'Remove team member?'}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody color={secondaryText}>
                  {memberToRemove?.uid === user?.uid ? (
                    <Alert status="warning" variant="subtle" borderRadius="lg">
                      <AlertIcon boxSize="22px" />
                      <Box>
                        <AlertTitle fontSize="md" color={textColor}>
                          Are you sure you want to leave?
                        </AlertTitle>
                        <AlertDescription
                          display="block"
                          mt={2}
                          color={leaveModalBodyColor}
                        >
                          You will lose access to this organization and its
                          data. If you are not a member of any other
                          organization, your sign-in for this account may be
                          permanently removed.
                        </AlertDescription>
                      </Box>
                    </Alert>
                  ) : (
                    <Text color={leaveModalBodyColor}>
                      Remove{' '}
                      <Text as="span" fontWeight="600">
                        {memberToRemove?.displayName || 'this user'}
                      </Text>{' '}
                      from your organization? They will lose access unless they
                      belong to another organization.
                    </Text>
                  )}
                </ModalBody>
                <ModalFooter gap={3}>
                  <Button
                    variant="ghost"
                    onClick={() => setMemberToRemove(null)}
                    isDisabled={removingUid !== null}
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="red"
                    onClick={() => void handleConfirmRemoveMember()}
                    isLoading={Boolean(
                      memberToRemove && removingUid === memberToRemove.uid,
                    )}
                    loadingText="Removing…"
                  >
                    {memberToRemove?.uid === user?.uid
                      ? 'Leave organization'
                      : 'Remove'}
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
            <Table variant="simple" color="gray.500" mb="24px">
              <Thead>
                <Tr>
                  <Th pe="10px" borderColor={borderColor}>
                    <Text
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color="gray.400"
                    >
                      MEMBER
                    </Text>
                  </Th>
                  <Th pe="10px" borderColor={borderColor} textAlign="center">
                    <Text
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color="gray.400"
                    >
                      ROLE
                    </Text>
                  </Th>
                  <Th pe="10px" borderColor={borderColor} textAlign="center">
                    <Text
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color="gray.400"
                    >
                      STATUS
                    </Text>
                  </Th>
                  <Th
                    pe="10px"
                    borderColor={borderColor}
                    textAlign="center"
                    w={{ base: '120px', md: '140px' }}
                  >
                    <Text
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color="gray.400"
                    >
                      ACTIONS
                    </Text>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row) => {
                  const isYou = isRowCurrentUser(row, user?.uid ?? undefined);
                  return (
                    <Tr
                      key={row.rowKey}
                      bg={isYou ? youRowBg : undefined}
                      _hover={{
                        cursor: 'pointer',
                      }}
                      transition="all 0.2s"
                    >
                      <Td
                        fontSize={{ sm: '14px' }}
                        minW={{ sm: '180px', md: '220px', lg: 'auto' }}
                        borderColor={borderColor}
                        verticalAlign="top"
                        borderLeftWidth={isYou ? '4px' : undefined}
                        borderLeftColor={isYou ? 'brand.400' : undefined}
                      >
                        <TeamMemberIdentityBlock
                          displayName={row.displayName}
                          email={row.email}
                          textColor={textColor}
                          secondaryText={secondaryText}
                          authLookupFailed={
                            row.kind === 'member' &&
                            Boolean(row.memberMeta.authLookupFailed)
                          }
                        />
                      </Td>
                      <Td
                        fontSize={{ sm: '14px' }}
                        minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                        borderColor={borderColor}
                        textAlign="center"
                      >
                        <TeamRoleTag label={row.roleLabel} />
                      </Td>
                      <Td
                        fontSize={{ sm: '14px' }}
                        minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                        borderColor={borderColor}
                        textAlign="center"
                      >
                        <TeamStatusTag label={row.statusLabel} />
                      </Td>
                      <Td
                        fontSize={{ sm: '14px' }}
                        borderColor={borderColor}
                        textAlign="center"
                        verticalAlign="middle"
                        w={{ base: '120px', md: '140px' }}
                      >
                        <HStack spacing={2} justify="center">
                          {row.kind === 'pending' ? (
                            <>
                              <Tooltip
                                label={
                                  isOrgAdmin
                                    ? 'Approve'
                                    : ORG_ADMIN_ONLY_TOOLTIP
                                }
                                placement="top"
                                openDelay={800}
                                shouldWrapChildren
                              >
                                <IconButton
                                  aria-label="Approve request"
                                  icon={<FiCheck />}
                                  size="sm"
                                  variant="ghost"
                                  color="green.500"
                                  bg="green.100"
                                  isLoading={actionId === row.requestId}
                                  isDisabled={!isOrgAdmin}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleAccept(row.requestId);
                                  }}
                                  _hover={{
                                    color: 'green.600',
                                    bg: 'green.50',
                                  }}
                                />
                              </Tooltip>
                              <Tooltip
                                label={
                                  isOrgAdmin
                                    ? 'Decline'
                                    : ORG_ADMIN_ONLY_TOOLTIP
                                }
                                placement="top"
                                openDelay={800}
                                shouldWrapChildren
                              >
                                <IconButton
                                  aria-label="Decline request"
                                  icon={<FiX />}
                                  size="sm"
                                  variant="ghost"
                                  bg="red.100"
                                  color="red.500"
                                  isLoading={actionId === row.requestId}
                                  isDisabled={!isOrgAdmin}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleDecline(row.requestId);
                                  }}
                                  _hover={{
                                    color: 'red.600',
                                    bg: 'red.50',
                                  }}
                                />
                              </Tooltip>
                            </>
                          ) : null}
                          {row.kind === 'member' &&
                          (isOrgAdmin || canRemoveMember(row.memberMeta)) ? (
                            <Menu
                              placement="bottom-end"
                              strategy="fixed"
                              closeOnSelect={false}
                            >
                              <MenuButton
                                as={IconButton}
                                aria-label="Member actions"
                                icon={<FiMoreVertical />}
                                size="sm"
                                variant="ghost"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <MenuList zIndex={2000}>
                                {isOrgAdmin ? (
                                  <ChangeRoleSubmenu
                                    member={row.memberMeta}
                                    updatingRoleUid={updatingRoleUid}
                                    onSelectRole={(role) =>
                                      void applyMemberRole(row.memberMeta, role)
                                    }
                                  />
                                ) : null}
                                {isOrgAdmin &&
                                canRemoveMember(row.memberMeta) ? (
                                  <MenuDivider />
                                ) : null}
                                {canRemoveMember(row.memberMeta) ? (
                                  <MenuItem
                                    color="red.500"
                                    closeOnSelect
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMemberToRemove(row.memberMeta);
                                    }}
                                  >
                                    {row.memberMeta.uid === user?.uid
                                      ? 'Leave organization'
                                      : 'Remove from organization'}
                                  </MenuItem>
                                ) : null}
                              </MenuList>
                            </Menu>
                          ) : null}
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Flex>
        )}
      </Card>
    </Box>
  );
};
